/**
 * LA LECTURE DE L'EXPORT D'ADMISSION SCODOC — dans le navigateur (lot E.3, révision du 2026-10-28)
 * ================================================================================================
 * Spec : doc/modules/etudiants.md § Révision du 2026-10-28 · maquette etudiants_maquette_v5.html (« Import SCODOC —
 * Étudiants »).
 *
 * Le fichier est lu ICI, et seule la LISTE BLANCHE validée quitte le poste (§0.6, comme APOGEE) : le domicile, le lieu de
 * naissance, le lycée, le téléphone fixe, le rapporteur, le commentaire, les notes et le score ne sont jamais envoyés.
 * Le serveur repasse chaque ligne par SA liste blanche (`etudiantsFusionService.nettoyerLigneScodoc`).
 *
 * Les intitulés sont ceux de la documentation ScoDoc (« Titres (variantes) »), comparés après normalisation (casse,
 * accents, espaces) — `code_nip` ou `NIP`, `emailperso` ou « mail personnel »… Les valeurs passent par les MÊMES
 * convertisseurs que l'import APOGEE (dates, téléphone, entiers) : deux fichiers, une seule forme en base.
 */
import { normaliserIntitule, decouperFeuille, matriceDepuisTexte, convertirDate, convertirTelephone, convertirEntier } from './apogeeImport';

/** Ce qui est lu : intitulés acceptés → champ envoyé au serveur. L'ordre est celui de l'écran. */
export const COLONNES_SCODOC_ETUDIANTS = [
    { cle: 'code_etudiant', intitules: ['code_nip', 'NIP'], libelle: 'numéro étudiant' },
    { cle: 'ine', intitules: ['code_ine', 'INE'], libelle: 'INE' },
    { cle: 'nom', intitules: ['nom'], libelle: 'nom' },
    { cle: 'prenom', intitules: ['prenom'], libelle: 'prénom' },
    { cle: 'sexe', intitules: ['civilite', 'sexe', 'genre'], libelle: 'civilité' },
    { cle: 'date_naissance', intitules: ['date_naissance'], libelle: 'date de naissance' },
    { cle: 'email', intitules: ['emailperso', 'mailperso', 'mail personnel', 'mail externe'], libelle: 'courriel personnel' },
    { cle: 'telephone', intitules: ['telephonemobile'], libelle: 'téléphone portable' },
    { cle: 'type_bac', intitules: ['bac'], libelle: 'type de bac' },
    { cle: 'annee_bac', intitules: ['annee_bac'], libelle: 'année du bac' },
    { cle: 'boursier', intitules: ['boursier'], libelle: 'boursier' },
    { cle: 'classement_recrutement', intitules: ['classement', 'classement global'], libelle: "rang d'admission" },
    { cle: 'statut_fichier', intitules: ['statut'], libelle: 'statut (contrôle du régime)' },
    { cle: 'groupes', intitules: ['groupes'], libelle: 'groupes' }
];

const texte = (v) => (v === null || v === undefined ? '' : String(v).replace(/\s+/g, ' ').trim());
const ouiNon01 = (v) => { const t = texte(v); return t === '1' ? true : (t === '0' ? false : null); };

/** Une cellule → la valeur envoyée (ou null). Le numéro étudiant garde sa forme brute : le serveur la contrôle. */
function convertir(cle, brut) {
    switch (cle) {
        case 'date_naissance': return convertirDate(brut);
        case 'telephone': return convertirTelephone(brut);
        case 'annee_bac': return convertirEntier(brut, 1990, 2035);
        case 'classement_recrutement': return convertirEntier(brut, 1, 9999);
        case 'boursier': return ouiNon01(brut);
        case 'sexe': { const t = texte(brut).toUpperCase(); return ['M', 'F', 'X'].includes(t) ? t : null; }
        case 'code_etudiant': return texte(brut).replace(/\.0+$/, '');
        default: return texte(brut) || null;
    }
}

/**
 * Une matrice (fichier ou collage) → `{ entetes, lignes, nbLignes, lues, nonLues, bloquant }`.
 * `lignes` = la liste blanche, une par étudiant, numérotée comme le fichier (ligne 2 = 1re donnée).
 */
export function analyserScodocEtudiants(matrice) {
    const { entetes, lignes } = decouperFeuille(matrice);
    const index = new Map(entetes.map((h, i) => [normaliserIntitule(h), i]));
    const lues = [];
    for (const c of COLONNES_SCODOC_ETUDIANTS) {
        const i = c.intitules.map((x) => index.get(normaliserIntitule(x))).find((x) => x !== undefined);
        if (i !== undefined) lues.push({ ...c, index: i, intitule: entetes[i] });
    }
    const indexLus = new Set(lues.map((c) => c.index));
    const nonLues = entetes.filter((h, i) => texte(h) && !indexLus.has(i));
    const manque = ['code_etudiant', 'nom', 'prenom', 'groupes'].filter((k) => !lues.some((c) => c.cle === k));
    const bloquant = !entetes.length ? 'Rien à lire : le fichier ou le collage est vide'
        : (manque.length ? `Colonnes introuvables : ${manque.map((k) => COLONNES_SCODOC_ETUDIANTS.find((c) => c.cle === k).intitules[0]).join(', ')} — est-ce bien l'export d'admission de SCODOC (avec sa ligne d'en-têtes) ?` : null);
    const pretes = lignes.map((l) => {
        const o = { ligne: l.numero };
        for (const c of lues) o[c.cle] = convertir(c.cle, l.cellules[c.index]);
        return o;
    });
    return { entetes, lignes: pretes, nbLignes: pretes.length, lues, nonLues, bloquant };
}

/** Le collage depuis Excel (tabulations), même chemin que le fichier. */
export function analyserCollageScodocEtudiants(texteColle) {
    return analyserScodocEtudiants(matriceDepuisTexte(texteColle));
}
